import { getCurrentUserId, getFirestoreDocId, updateActivityProgress } from './firebase';

const completionStorageKey = (conversationId) => (
  conversationId ? `activityCompleted:${conversationId}` : null
);

export const getLearningPathActivityId = (selectedActivity) => {
  if (!selectedActivity || typeof selectedActivity !== 'string') {
    return null;
  }

  const match = selectedActivity.match(/^learning-path-(\d+)$/);
  return match ? match[1] : null;
};

/**
 * Activities that show the in-session progress bar. Learning-path attempts
 * are persisted server-side; onboarding progress is display-only (driven by
 * the chat LLM's per-turn value, never written to the progress endpoint).
 */
export const shouldShowSessionProgress = (selectedActivity) => (
  !!getLearningPathActivityId(selectedActivity) || selectedActivity === 'onboarding-intro'
);

const sendActivityProgressEvent = async (selectedActivity, payload, errorLabel) => {
  const activityId = getLearningPathActivityId(selectedActivity);
  if (!activityId) {
    return null;
  }

  const userId = getCurrentUserId();
  const conversationId = getFirestoreDocId();
  if (!userId || !conversationId) {
    console.warn(`Missing userId or conversationId for ${errorLabel}`);
    return null;
  }

  try {
    return await updateActivityProgress(userId, activityId, conversationId, payload);
  } catch (error) {
    console.error(`Error ${errorLabel}:`, error);
    return null;
  }
};

/**
 * Marks the start of a new attempt (one conversation = one attempt, always
 * starting at 0 — attempts are redone, never continued).
 */
export const trackActivityStart = (selectedActivity) => (
  sendActivityProgressEvent(selectedActivity, { event: 'start' }, 'tracking activity start')
);

/**
 * Per-turn progress from the chat LLM's conversation_progress (legacy /upload mode).
 */
export const trackActivityProgress = (selectedActivity, progressIndex) => (
  sendActivityProgressEvent(selectedActivity, { event: 'progress', progressIndex }, 'tracking activity progress')
);

/**
 * Throttled transcript submission for a server-side Gemini estimate (realtime mode).
 */
export const trackActivityProgressFromTranscript = (selectedActivity, messages) => (
  sendActivityProgressEvent(selectedActivity, { event: 'progress', messages }, 'estimating activity progress')
);

/**
 * Marks the attempt ended without changing progress (short sessions that skip /end).
 */
export const endActivityAttempt = (selectedActivity) => (
  sendActivityProgressEvent(selectedActivity, { event: 'end' }, 'ending activity attempt')
);

export const markActivityCompletionForCurrentConversation = () => {
  const conversationId = getFirestoreDocId();
  const key = completionStorageKey(conversationId);
  if (key) {
    sessionStorage.setItem(key, 'true');
  }
};

export const isActivityCompletionRecorded = (conversationId = getFirestoreDocId()) => {
  const key = completionStorageKey(conversationId);
  return !!(key && sessionStorage.getItem(key) === 'true');
};

export const clearActivityCompletionRecord = (conversationId = getFirestoreDocId()) => {
  const key = completionStorageKey(conversationId);
  if (key) {
    sessionStorage.removeItem(key);
  }
};

export const trackActivityCompletion = async (selectedActivity) => {
  if (!getLearningPathActivityId(selectedActivity)) {
    return null;
  }

  markActivityCompletionForCurrentConversation();
  return sendActivityProgressEvent(selectedActivity, { event: 'complete' }, 'tracking activity completion');
};
